import { useState, useEffect, useCallback } from 'react';
import { timeProvider, type TimeInfo } from '../services/TimeProvider';

/** The frontend runs in DEV/stage - the time control UI and API calls are allowed. */
export const isTimeControlAvailable =
  import.meta.env.DEV === true ||
  import.meta.env.MODE === 'development' ||
  import.meta.env.MODE === 'stage';

export function useTime() {
  const [currentTime, setCurrentTime] = useState<Date>(() => new Date());
  const [timeInfo, setTimeInfo] = useState<TimeInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshTime = useCallback(async () => {
    setIsLoading(true);
    try {
      const time = await timeProvider.getCurrentTime();
      setCurrentTime(time);

      const info = await timeProvider.getTimeInfo();
      setTimeInfo(info);
    } catch (error) {
      console.error('Failed to refresh time', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTime();
  }, [refreshTime]);

  const setMockTime = useCallback(
    async (time: Date | string) => {
      setIsLoading(true);
      try {
        const info = await timeProvider.setMockTime(time);
        setTimeInfo(info);
        setCurrentTime(new Date(info.mockTime));
      } catch (error) {
        console.error('Failed to set mock time', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const resetMockTime = useCallback(async () => {
    setIsLoading(true);
    try {
      const info = await timeProvider.resetMockTime();
      setTimeInfo(info);
      setCurrentTime(new Date(info.mockTime));
    } catch (error) {
      console.error('Failed to reset mock time', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const advanceTime = useCallback(
    async (hours: number = 0, minutes: number = 0, seconds: number = 0) => {
      setIsLoading(true);
      try {
        const info = await timeProvider.advanceTime(hours, minutes, seconds);
        setTimeInfo(info);
        setCurrentTime(new Date(info.mockTime));
      } catch (error) {
        console.error('Failed to advance time', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    currentTime,
    timeInfo,
    isLoading,
    refreshTime,
    setMockTime,
    resetMockTime,
    advanceTime,
    isMocked: timeInfo?.isMocked ?? false,
    isTimeControlAvailable,
  };
}
